import { useEffect, useState, type FormEvent } from "react";
import { LOGISTICS_FIELDS, LOGISTICS_LABELS, type EventLogistics, type LogisticsField, type LogisticsResponse, type LogisticsValues } from "../../shared/logistics";
import { api, ApiError, put } from "../api";

function dateLabel(value: string | null) {
  return value ? new Intl.DateTimeFormat("en-CA", { dateStyle: "medium", timeZone: "America/Toronto" }).format(new Date(`${value}T12:00:00Z`)) : "Not set";
}

function timeLabel(value: string) {
  return new Intl.DateTimeFormat("en-CA", { dateStyle: "medium", timeStyle: "short", timeZone: "America/Toronto" }).format(new Date(value));
}

function valuesOf(logistics: EventLogistics): LogisticsValues {
  return Object.fromEntries(LOGISTICS_FIELDS.map((field) => [field, logistics[field]])) as LogisticsValues;
}

export function EventLogisticsPanel({ invoiceId, active, eventDate, eventLocation }: {
  invoiceId: string; active: boolean; eventDate: string | null; eventLocation: string | null;
}) {
  const [data, setData] = useState<LogisticsResponse | null>(null);
  const [draft, setDraft] = useState<LogisticsValues | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [message, setMessage] = useState("");
  const dirty = Boolean(draft && data && LOGISTICS_FIELDS.some((field) => (draft[field]?.trim() || null) !== data.logistics[field]));

  async function load() {
    setBusy(true);
    setError("");
    try {
      setData(await api<LogisticsResponse>(`/api/invoices/${encodeURIComponent(invoiceId)}/logistics`));
      setDraft(null);
      setConflict(false);
    } catch (err) { setError(err instanceof Error ? err.message : "Event logistics could not be loaded."); }
    finally { setBusy(false); }
  }

  useEffect(() => { void load(); }, [invoiceId]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  function edit() {
    if (!data) return;
    setDraft(valuesOf(data.logistics));
    setMessage("");
    setError("");
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!data || !draft) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await put<LogisticsResponse & { unchanged?: boolean }>(`/api/invoices/${encodeURIComponent(invoiceId)}/logistics`, {
        ...draft, edit_version: data.logistics.edit_version, previous: valuesOf(data.logistics)
      });
      setData(result);
      setDraft(null);
      setConflict(false);
      setMessage(result.unchanged ? "No changes to save." : "Logistics saved. No invoice publication needed.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Event logistics could not be saved.");
      setConflict(err instanceof ApiError && err.status === 409);
    } finally { setBusy(false); }
  }

  const fieldValue = (_field: LogisticsField, value: string | null) => value || "Not set";
  return <section className="panel detail-card detail-wide logistics-panel" aria-labelledby="logistics-title">
    <div className="panel-heading"><div><span className="section-kicker">Internal planning</span><h2 id="logistics-title">Event Logistics</h2></div>
      {!draft && active && data && <button className="button secondary" type="button" onClick={edit} disabled={busy}>Edit logistics</button>}
    </div>
    <div className="logistics-body">
      <div className="logistics-intro"><span className="logistics-private">Dashboard only</span><p>Event date and location are saved with the invoice. Keep the latest delivery, pickup, and event details here.</p></div>
      <dl className="logistics-event-summary"><div><dt>Event date</dt><dd>{dateLabel(eventDate)}</dd></div><div><dt>Event location</dt><dd>{eventLocation || "Not set"}</dd></div></dl>
      {error && <div className="notice error" role="alert">{error}{conflict && <button className="text-button" type="button" disabled={busy} onClick={() => {
        if (!dirty || window.confirm("Discard your unsaved logistics edits and load the latest saved details?")) void load();
      }}>Reload latest details</button>}{!data && <button className="text-button" type="button" disabled={busy} onClick={() => void load()}>Try again</button>}</div>}
      {message && <p className="logistics-success" role="status">✓ {message}</p>}
      {!data && !error && <p className="logistics-help" role="status">Loading event logistics…</p>}
      {data && (draft ? <form onSubmit={save}>
        <fieldset className="logistics-fields" disabled={busy || !active}>
          <div className="logistics-two-fields"><label>Delivery details<textarea rows={3} maxLength={5000} value={draft.delivery_details || ""} onChange={(event) => setDraft({ ...draft, delivery_details: event.target.value || null })} placeholder="Delivery date, time window, access, and setup instructions" /></label><label>Pickup details<textarea rows={3} maxLength={5000} value={draft.pickup_details || ""} onChange={(event) => setDraft({ ...draft, pickup_details: event.target.value || null })} placeholder="Pickup date, time window, and collection instructions" /></label></div>
          <label>Event details<textarea rows={3} maxLength={15000} value={draft.notes || ""} onChange={(event) => setDraft({ ...draft, notes: event.target.value || null })} placeholder="Schedule, room details, on-site contact, crew notes, or other reminders" /><small>Visible only to your team.</small></label>
        </fieldset>
        <div className="logistics-footer"><span>{dirty ? "Unsaved changes" : "No changes yet"}</span><div><button className="button ghost" type="button" disabled={busy} onClick={() => {
          if (!dirty || window.confirm("Discard your unsaved logistics changes?")) { setDraft(null); setError(""); setConflict(false); }
        }}>Cancel</button><button className="button primary" type="submit" disabled={busy || !dirty || conflict || !active}>{busy ? "Saving…" : "Save logistics"}</button></div></div>
      </form> : <>
        <dl className="logistics-values">{LOGISTICS_FIELDS.map((field) => <div key={field} className={field === "notes" ? "logistics-notes" : ""}><dt>{LOGISTICS_LABELS[field]}</dt><dd className={data.logistics[field] ? "" : "logistics-unset"}>{fieldValue(field, data.logistics[field])}</dd></div>)}</dl>
        <p className="logistics-updated">{data.logistics.updated_at ? <>Updated {timeLabel(data.logistics.updated_at)} (Toronto) by {data.logistics.updated_by_email}</> : "No internal logistics changes yet."}{!active && " · Canceled invoice — read-only."}</p>
      </>)}
      {data && data.history.length > 0 && <details className="logistics-history"><summary>Change history <span>{data.history.length === 50 ? "Latest 50 updates" : `${data.history.length} ${data.history.length === 1 ? "update" : "updates"}`}</span></summary>
        {data.history.map((entry) => <details className="logistics-history-entry" key={entry.edit_version}><summary><strong>{timeLabel(entry.changed_at)} (Toronto)</strong><span>{entry.changed_by_email} · {LOGISTICS_FIELDS.filter((field) => entry.previous[field] !== entry.saved[field]).map((field) => LOGISTICS_LABELS[field]).join(", ")}</span></summary>
          <div className="logistics-changes">{LOGISTICS_FIELDS.filter((field) => entry.previous[field] !== entry.saved[field]).map((field) => <div key={field}><strong>{LOGISTICS_LABELS[field]}</strong><div><span>Before</span><p>{fieldValue(field, entry.previous[field])}</p></div><div><span>After</span><p>{fieldValue(field, entry.saved[field])}</p></div></div>)}</div>
        </details>)}
      </details>}
    </div>
  </section>;
}
